import { useState, useMemo } from 'react'
import { useNavigate, useRouter } from '@tanstack/react-router'
import { create } from '@bufbuild/protobuf'
import { timestampFromDate, type Timestamp } from '@bufbuild/protobuf/wkt'
import { KanbanBoard, KanbanColumn } from '@/components/shared/kanban'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { InquiryStageEnum } from '@/gen/cortado/v1/settings_pb'
import { InquiryLiteSchema } from '@/gen/cortado/v1/inquiries_pb'
import { InquirySheet } from '@/components/inquiries/inquiry-sheet'
import { EventTypeBadge, InquiryTagList, InquiryTagLegend } from '@/components/shared/enum-badge'
import { UserAvatar } from '@/components/shared/user-avatar'
import { formatDateTime, formatShortDate, formatTimestampRelativeLong, formatTimestampWithTime } from '@/lib/date-utils'
import { formatFullName } from '@/lib/utils'
import { ArrowUpDown, Maximize2, Minimize2 } from 'lucide-react'
import { useStageLeadsList, useLeadsCount, type StageLeadsFilterOptions } from '@/stores/leads.store'
import { leadsFiltersActions } from '@/stores/leads-filters.store'
import type { Lead } from './types'
import { mapInquiryLiteToLead } from './types'
import { isAcceptedWithoutEvent } from '@/lib/leads'
import { LeadContextMenu } from './lead-context-menu'
import { badgeVariantToTailwind } from './stage-colors'
import { LeadMailBadge, withoutMailTags } from './lead-mail-badge'

export interface LeadsKanbanColumnDef {
  status: number
  label: string
  color: string
  /** false = closed stage under Active scope: drop target only, no card fetching. */
  fetchCards: boolean
}

const SAMPLE_COLUMN_LEADS: Lead[] = [
  { contactFirstName: 'Jordan', contactLastName: 'Ellis', contactCompanyName: 'Harbor & Vine Catering', eventTypeKey: 'Wedding' },
  { contactFirstName: 'Priya', contactLastName: 'Ramanathan', eventTypeKey: 'Corporate' },
  { contactFirstName: 'Marcus', contactLastName: 'Okafor', contactCompanyName: 'Northfield Events', eventTypeKey: 'Birthday' },
].map((contact, i) =>
  mapInquiryLiteToLead(
    create(InquiryLiteSchema, {
      ...contact,
      id: `sample-${i}`,
      eventsCount: 1,
      totalGuestCount: 120,
      quoteTotal: 450000,
      firstEventDate: timestampFromDate(new Date(2026, 5, 12 + i)),
      createdAt: timestampFromDate(new Date(2026, 4, 2 + i)),
      assignedTo: 'sample-owner',
      assignedToFirstName: 'Maya',
      assignedToLastName: 'Chen',
    }),
  ),
)

/** How lead cards are ordered within each kanban column. */
export type LeadSortOption = 'received_desc' | 'received_asc' | 'event_asc' | 'event_desc'

const LEAD_SORT_OPTIONS: { value: LeadSortOption; label: string }[] = [
  { value: 'received_desc', label: 'Date received (newest)' },
  { value: 'received_asc', label: 'Date received (oldest)' },
  { value: 'event_asc', label: 'Event date (soonest)' },
  { value: 'event_desc', label: 'Event date (latest)' },
]

/** A card's tags: the mail-driven Waiting / Reply needed as words, the rest as icons. */
function LeadCardTags({ lead }: { lead: Lead }) {
  const otherTags = withoutMailTags(lead.tags)
  const hasMailTag = otherTags.length < (lead.tags ?? []).filter(t => t !== 0).length
  if (!hasMailTag && otherTags.length === 0) return null
  return (
    <div className="flex items-center gap-1">
      <LeadMailBadge
        tags={lead.tags}
        lastInboundAt={lead.lastInboundAt}
        lastOutboundAt={lead.lastOutboundAt}
        lastMessageDirection={lead.lastMessageDirection}
      />
      {otherTags.length > 0 && <InquiryTagList tags={otherTags} compact />}
    </div>
  )
}

/** Milliseconds since epoch for a proto Timestamp, or undefined when unset. */
function leadTimestampMillis(ts?: Timestamp): number | undefined {
  if (!ts) return undefined
  return Number(ts.seconds) * 1000 + Math.floor(ts.nanos / 1_000_000)
}

/**
 * Order leads within a column by the selected date. Leads without the relevant
 * date always sort last (received falls back to created-at; event options
 * render such leads as "No date").
 */
function sortLeads(leads: Lead[], sortBy: LeadSortOption): Lead[] {
  const sorted = [...leads]
  sorted.sort((a, b) => {
    if (sortBy === 'received_desc' || sortBy === 'received_asc') {
      const av = leadTimestampMillis(a.createdAt)
      const bv = leadTimestampMillis(b.createdAt)
      if (av === undefined && bv === undefined) return 0
      if (av === undefined) return 1
      if (bv === undefined) return -1
      return sortBy === 'received_asc' ? av - bv : bv - av
    }
    const av = leadTimestampMillis(a.firstEventDate)
    const bv = leadTimestampMillis(b.firstEventDate)
    if (av === undefined && bv === undefined) return 0
    if (av === undefined) return 1
    if (bv === undefined) return -1
    return sortBy === 'event_asc' ? av - bv : bv - av
  })
  return sorted
}

interface LeadsKanbanProps {
  columns: LeadsKanbanColumnDef[]
  filterOptions: StageLeadsFilterOptions
  onItemMove?: (itemId: string, newStatus: number, oldStatus: number, item?: Lead) => Promise<void>
  stages: InquiryStageEnum[]
  onStageChange: (lead: Lead, newStage: InquiryStageEnum) => void | Promise<void>
}

const renderLeadCard = (lead: Lead, isLoading: boolean, compactView: boolean = false) => {
  const name = formatFullName(lead.contact?.firstName, lead.contact?.lastName)
  const showNoEventYet = isAcceptedWithoutEvent({ stage: lead.stage, bookingId: lead.bookingId })

  // Format quote total (stored in cents, convert to dollars)
  const formatQuoteTotal = (total: number | undefined) => {
    if (total === undefined || total === null) return null
    const dollars = total / 100
    return `$${Math.round(dollars).toLocaleString()}`
  }

  // Compact view: Event Date - Contact Name + Footer
  if (compactView) {
    return (
      <div className={`rounded-lg border p-3 hover:bg-muted/50 transition-colors ${isLoading || lead.archivedAt ? 'opacity-60' : ''}`}>
        <div className="space-y-2">
          {/* Header with Event Date - Contact Name and Quote Total */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <div className="text-sm font-medium">
                {lead.firstEventDate
                  ? (lead.eventsCount > 1 && lead.lastEventDate
                      ? `${formatShortDate(lead.firstEventDate, lead.firstEventTimezone ?? lead.events?.[0]?.eventTimezone)} - ${formatShortDate(lead.lastEventDate, lead.events?.[lead.events.length - 1]?.eventTimezone)}`
                      : formatDateTime(lead.firstEventDate, lead.firstEventTimezone ?? lead.events?.[0]?.eventTimezone))
                  : 'No date'} - {name}
              </div>
              {lead.contactCompanyName && (
                <div className="text-xs text-muted-foreground">{lead.contactCompanyName}</div>
              )}
              {lead.archivedAt && (
                <Badge variant="secondary" className="mt-1">Archived</Badge>
              )}
              {showNoEventYet && (
                <Badge variant="warning" className="mt-1">No event yet</Badge>
              )}
            </div>
            {lead.quoteTotal !== undefined && (
              <Badge variant="secondary" className="shrink-0">
                {formatQuoteTotal(lead.quoteTotal)}
              </Badge>
            )}
          </div>

          {/* Tags and Received timestamp row */}
          <div className="flex items-center justify-between gap-2">
            {/* Tags (compact single-letter) */}
            <LeadCardTags lead={lead} />

            {(lead.createdAt || lead.assignedTo) && (
              <div className="flex items-center gap-2 ml-auto">
                {/* Received timestamp */}
                {lead.createdAt && (
                  <TooltipProvider>
                    <Tooltip delayDuration={0}>
                      <TooltipTrigger asChild>
                        <span className="text-xs text-muted-foreground cursor-help">
                          {formatTimestampRelativeLong(lead.createdAt)}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="bottom">
                        {formatTimestampWithTime(lead.createdAt)}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}

                {/* Owner */}
                {lead.assignedTo && (
                  <UserAvatar firstName={lead.assignedToFirstName} lastName={lead.assignedToLastName} size="sm" tooltip />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Full view: All details
  return (
    <div className={`rounded-lg border p-3 hover:bg-muted/50 transition-colors ${isLoading || lead.archivedAt ? 'opacity-60' : ''}`}>
      <div className="space-y-2">
        {/* Header with Date/Time and Event Type + Quote Total */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            {/* Date Range */}
            {lead.firstEventDate && (
              <div className="text-sm font-medium">
                {lead.eventsCount > 1 && lead.lastEventDate
                  ? `${formatShortDate(lead.firstEventDate, lead.firstEventTimezone ?? lead.events?.[0]?.eventTimezone)} - ${formatShortDate(lead.lastEventDate, lead.events?.[lead.events.length - 1]?.eventTimezone)}`
                  : formatDateTime(lead.firstEventDate, lead.firstEventTimezone ?? lead.events?.[0]?.eventTimezone)}
              </div>
            )}

            {/* Name */}
            <div className="mt-2">
              <div className="font-semibold text-sm">{name}</div>
              {lead.contactCompanyName && (
                <div className="text-xs text-muted-foreground">{lead.contactCompanyName}</div>
              )}
              {lead.archivedAt && (
                <Badge variant="secondary" className="mt-1">Archived</Badge>
              )}
              {showNoEventYet && (
                <Badge variant="warning" className="mt-1">No event yet</Badge>
              )}
            </div>
          </div>

          {/* Event Type and Quote Total - stacked on right */}
          <div className="flex flex-col items-end gap-1 min-w-0 max-w-[45%]">
            {lead.eventType && (
              <EventTypeBadge type={lead.eventType} className="text-xs" />
            )}
            {lead.quoteTotal !== undefined && (
              <Badge variant="secondary">
                {formatQuoteTotal(lead.quoteTotal)}
              </Badge>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap text-xs text-muted-foreground">
          {lead.eventsCount > 0 && (
            <div>{lead.eventsCount} event{lead.eventsCount === 1 ? '' : 's'}</div>
          )}
          {lead.eventsCount > 0 && lead.totalGuestCount > 0 && (
            <span>·</span>
          )}
          {lead.totalGuestCount > 0 && (
            <div>{lead.totalGuestCount} guest{lead.totalGuestCount === 1 ? '' : 's'}</div>
          )}
        </div>

        {/* Footer: Received timestamp and Tags */}
        {(lead.createdAt || (lead.tags && lead.tags.length > 0) || lead.assignedTo) && (
          <div className="pt-1 border-t flex items-center justify-between gap-2">
            {/* Received timestamp */}
            {lead.createdAt && (
              <TooltipProvider>
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <span className="text-xs text-muted-foreground cursor-help">
                      Received {formatTimestampRelativeLong(lead.createdAt)}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">
                    {formatTimestampWithTime(lead.createdAt)}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {(lead.tags && lead.tags.length > 0 || lead.assignedTo) && (
              <div className="flex items-center gap-2 ml-auto">
                {/* Tags */}
                <LeadCardTags lead={lead} />

                {/* Owner */}
                {lead.assignedTo && (
                  <UserAvatar firstName={lead.assignedToFirstName} lastName={lead.assignedToLastName} size="sm" tooltip />
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * One kanban column backed by its own per-stage query. Rendered as one
 * component instance per stage so each column's hooks run independently
 * (rules-of-hooks safe — the set of columns is stable per render).
 */
function LeadsKanbanColumn({
  column,
  filterOptions,
  sortBy,
}: {
  column: LeadsKanbanColumnDef
  filterOptions: StageLeadsFilterOptions
  sortBy: LeadSortOption
}) {
  const stage = column.status as InquiryStageEnum

  // Card query — disabled for drop-target-only columns (closed stages under Active scope).
  const stageQuery = useStageLeadsList(stage, { ...filterOptions, enabled: column.fetchCards })

  // Count-only query for drop-target-only columns with the same server filters.
  const { count, hasLoaded: countHasLoaded } = useLeadsCount({
    stages: [stage],
    eventTypes: filterOptions.eventTypes,
    since: filterOptions.since,
    createdBefore: filterOptions.createdBefore,
    eventDateStart: filterOptions.eventDateStart,
    eventDateEnd: filterOptions.eventDateEnd,
    search: filterOptions.search,
    sourceChannels: filterOptions.sourceChannels,
    includeArchived: filterOptions.includeArchived,
    enabled: !column.fetchCards,
  })

  // Not fetching (closed stage under Active scope): never show cards from the
  // query's cache, even if a prior "All" scope visit loaded and cached rows.
  const items = useMemo(() => {
    if (!column.fetchCards) return []
    // stageQuery.inquiries only holds the pages loaded so far (infinite query,
    // 25/page), so this sort orders loaded cards only — it's a partial ordering
    // over the column, not the full set. Loading more pages can insert cards
    // ahead of ones already shown.
    return sortLeads(stageQuery.inquiries.map(mapInquiryLiteToLead), sortBy)
  }, [column.fetchCards, stageQuery.inquiries, sortBy])

  return (
    <KanbanColumn<Lead, number>
      column={column}
      items={items}
      totalCount={column.fetchCards ? stageQuery.totalCount : count}
      loadedCount={column.fetchCards ? stageQuery.inquiries.length : 0}
      loading={{
        hasLoaded: column.fetchCards ? stageQuery.hasLoaded : countHasLoaded,
        id: `leads:column:${column.status}`,
        // A drop-target-only column draws its placeholder, not cards.
        sample: column.fetchCards ? SAMPLE_COLUMN_LEADS : [],
      }}
      footer={
        column.fetchCards && stageQuery.hasMore ? (
          <Button
            variant="ghost"
            size="sm"
            className="w-full shrink-0"
            disabled={stageQuery.isFetchingNextPage}
            onClick={() => stageQuery.fetchNextPage()}
          >
            Load more ({stageQuery.inquiries.length} of {stageQuery.totalCount})
          </Button>
        ) : undefined
      }
      placeholder={
        !column.fetchCards ? (
          <div className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border px-4 py-6 text-center">
            <p className="text-sm text-muted-foreground">
              {countHasLoaded && count > 0
                ? `${count} ${column.label.toLowerCase()} lead${count === 1 ? '' : 's'} hidden by the Active filter`
                : `${column.label} leads are hidden by the Active filter`}
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => leadsFiltersActions.setStageScope('all')}
            >
              Show all stages
            </Button>
            <p className="text-xs text-muted-foreground">
              Or drop a lead here to move it to {column.label}
            </p>
          </div>
        ) : undefined
      }
    />
  )
}

export function LeadsKanban({
  columns,
  filterOptions,
  onItemMove,
  stages,
  onStageChange,
}: LeadsKanbanProps) {
  // Local state (not persisted)
  const [compactView, setCompactView] = useState(false)
  const [sortBy, setSortBy] = useState<LeadSortOption>('received_desc')
  const [createSheetOpen, setCreateSheetOpen] = useState(false)
  const [createSheetDefaultStage, setCreateSheetDefaultStage] = useState<InquiryStageEnum>(InquiryStageEnum.INQUIRY_STAGE_NEW)
  const navigate = useNavigate()
  const router = useRouter()

  // Prefetch full inquiry data on hover for instant navigation
  const handlePrefetch = (inquiryId: string) => {
    void router.preloadRoute({ to: '/leads/$id', params: { id: inquiryId } })
  }

  const toggleCompactView = () => {
    setCompactView(!compactView)
  }

  const handleCardClick = (itemId: string) => {
    navigate({ to: '/leads/$id', params: { id: itemId } })
  }

  const handleAddItem = (status: number) => {
    // Status is the InquiryStageEnum value for the column
    setCreateSheetDefaultStage(status as InquiryStageEnum)
    setCreateSheetOpen(true)
  }

  return (
    <div className="flex flex-col flex-1 min-h-0 space-y-4">
      {/* Tag Legend, Sort control and Compact View Toggle */}
      <div className="flex items-center justify-end flex-shrink-0">
        <div className="flex items-center gap-4">
          <InquiryTagLegend />
          <Select value={sortBy} onValueChange={(value) => setSortBy(value as LeadSortOption)}>
            <SelectTrigger size="sm" className="w-[190px]" aria-label="Sort leads by">
              <ArrowUpDown className="size-4" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LEAD_SORT_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={toggleCompactView}
            className="h-8"
          >
            {compactView ? (
              <>
                <Maximize2 className="size-4" />
                Expand
              </>
            ) : (
              <>
                <Minimize2 className="size-4" />
                Compact
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Kanban Board */}
      <div className="flex-1 min-h-0">
        <KanbanBoard<Lead, number>
          columns={columns}
          onItemMove={onItemMove}
          onCardClick={handleCardClick}
          onCardHover={(itemId) => handlePrefetch(itemId)}
          onAddItem={handleAddItem}
          renderCard={(lead, isLoading) => (
            <LeadContextMenu
              lead={lead}
              stages={stages}
              onStageChange={onStageChange}
            >
              {renderLeadCard(lead, isLoading, compactView)}
            </LeadContextMenu>
          )}
        >
          {columns.map((column) => (
            <LeadsKanbanColumn
              key={column.status}
              column={column}
              filterOptions={filterOptions}
              sortBy={sortBy}
            />
          ))}
        </KanbanBoard>
      </div>

      {/* Create Lead Sheet */}
      <InquirySheet
        mode="create"
        open={createSheetOpen}
        onOpenChange={setCreateSheetOpen}
        defaultStage={createSheetDefaultStage}
      />
    </div>
  )
}

// Export utility function for use in parent component
export { badgeVariantToTailwind }

// Exported for focused testing of card rendering without mounting the full
// store-backed board.
export { renderLeadCard }
